import type { StatusWindow } from "helpers/maintenance/maintenance-state";
import { endMs, fmtClock, fmtLeft, fmtTime, progressPct } from "./MaintenanceBanner";
import { useServerNow } from "./useMaintenance";

export function MaintenanceFullPage({
  window: w,
  productName,
  onCheck,
}: {
  window: StatusWindow;
  productName: string;
  onCheck: () => void;
}) {
  const end = endMs(w);
  const now = useServerNow(end !== null);
  const update = w.latestUpdate;

  return (
    <main className="mw-page">
      <div className="mw-blob-a" aria-hidden="true" />
      <div className="mw-blob-b" aria-hidden="true" />
      <div className="mw-dots" aria-hidden="true" />
      <div className="mw-page-body text-center">
        <svg viewBox="0 0 160 120" className="mw-gears" aria-hidden="true">
          <defs>
            <linearGradient id="mw-g1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#a78bfa" />
              <stop offset="1" stopColor="#f472b6" />
            </linearGradient>
            <linearGradient id="mw-g2" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#22d3ee" />
              <stop offset="1" stopColor="#6366f1" />
            </linearGradient>
          </defs>
          <g className="mw-gear">
            <path
              fill="url(#mw-g1)"
              d="M62 20l6 0 2 9 6 3 8-5 4 4-5 8 3 6 9 2 0 6-9 2-3 6 5 8-4 4-8-5-6 3-2 9-6 0-2-9-6-3-8 5-4-4 5-8-3-6-9-2 0-6 9-2 3-6-5-8 4-4 8 5 6-3z"
            />
            <circle cx="65" cy="52" r="10" fill="#1e1b4b" />
          </g>
          <g className="mw-gear-rev">
            <path
              fill="url(#mw-g2)"
              d="M108 58l5 0 1 7 5 2 6-4 3 3-4 6 2 5 7 1 0 5-7 1-2 5 4 6-3 3-6-4-5 2-1 7-5 0-1-7-5-2-6 4-3-3 4-6-2-5-7-1 0-5 7-1 2-5-4-6 3-3 6 4 5-2z"
            />
            <circle cx="110.5" cy="83" r="7" fill="#1e1b4b" />
          </g>
        </svg>
        <p className="mw-badge">
          <span className="mw-ping" aria-hidden="true" />
          SCHEDULED MAINTENANCE
        </p>
        <h1 className="mw-title">We&apos;re upgrading {productName}</h1>
        {w.message && <p className="mw-message">{w.message}</p>}
        <div className="mw-card">
          {end === null ? (
            <p className="mw-asap mb-0">We&apos;ll be back as soon as possible</p>
          ) : (
            <>
              <div className="d-flex align-items-end justify-content-between gap-3">
                <div className="text-start">
                  <p className="mw-label mb-0">Expected back by</p>
                  <p className="mw-back-time mb-0">
                    {fmtTime(end)} <span className="mw-label-sm">your time</span>
                  </p>
                </div>
                <p className="mw-count mb-0" aria-hidden="true">
                  {fmtClock(end - now)}
                </p>
                <span className="visually-hidden" role="status">
                  {fmtLeft(end - now)} left
                </span>
              </div>
              <div className="mw-progress" aria-hidden="true">
                <div className="mw-shimmer" style={{ width: `${progressPct(w, now)}%` }} />
              </div>
            </>
          )}
        </div>
        {update && (
          <div className="mw-latest text-start">
            <p className="mw-latest-head mb-1">Latest update</p>
            <p className="mb-0">
              <span className="mw-label me-2">{fmtTime(Date.parse(update.at))}</span>
              {update.message}
            </p>
          </div>
        )}
        <p className="mw-foot">This page checks every minute and opens the app as soon as we&apos;re back.</p>
        <button type="button" onClick={onCheck} className="mw-check">
          Check now
        </button>
      </div>
    </main>
  );
}
